import React, { useEffect, useRef, useState } from 'react';

import {
  JourneyStagesState,
  PipelinePredictState,
  QualificationScoringState,
  SectionState,
} from 'types/sideBarTypes';

import 'app/App.scss';

const Sidebar = () => {
  // State for Journey Stages, Pipeline Predict, and Qualification Scoring
  const [journeyStages, setJourneyStages] = useState<JourneyStagesState>({
    topFunnel: true,
    midFunnel: false,
    botFunnel: false,
  });

  const [pipelinePredict, setPipelinePredict] = useState<PipelinePredictState>({
    highlyLikely: true,
    likely: false,
    unlikely: false,
  });

  const [qualificationScoring, setQualificationScoring] = useState<QualificationScoringState>({
    highlyQualified: true,
    qualified: false,
    unqualified: false,
  });

  const [isJourneyStagesExpanded, setIsJourneyStagesExpanded] = useState(false);
  const [isPipelinePredictExpanded, setIsPipelinePredictExpanded] = useState(false);
  const [isQualificationScoringExpanded, setIsQualificationScoringExpanded] = useState(false);

  const journeyStagesRef = useRef<HTMLInputElement | null>(null);
  const pipelinePredictRef = useRef<HTMLInputElement | null>(null);
  const qualificationScoringRef = useRef<HTMLInputElement | null>(null);

  // Partial selection logic
  const areSomeSelected = (section: SectionState) =>
    Object.values(section).some(Boolean) && !Object.values(section).every(Boolean);
  const areAllSelected = (section: SectionState) => Object.values(section).every(Boolean);

  useEffect(() => {
    if (journeyStagesRef.current) {
      journeyStagesRef.current.indeterminate = areSomeSelected(journeyStages);
    }
    if (pipelinePredictRef.current) {
      pipelinePredictRef.current.indeterminate = areSomeSelected(pipelinePredict);
    }
    if (qualificationScoringRef.current) {
      qualificationScoringRef.current.indeterminate = areSomeSelected(qualificationScoring);
    }
  }, [journeyStages, pipelinePredict, qualificationScoring]);

  return (
    <div className='QueryExplorer__sidebar'>
      {/* Journey Stages */}
      <div className='collapsible-section'>
        <a href='/journey-stages'>Journey Stages</a>
        <div
          className='collapsible-header'
          role={'button'}
          tabIndex={0}
          onClick={() => setIsJourneyStagesExpanded(!isJourneyStagesExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsJourneyStagesExpanded(!isJourneyStagesExpanded);
            }
          }}
        >
          <input
            ref={journeyStagesRef}
            type='checkbox'
            checked={areAllSelected(journeyStages)}
            onChange={() => {
              const newState = !areAllSelected(journeyStages);
              setJourneyStages({
                topFunnel: newState,
                midFunnel: newState,
                botFunnel: newState,
              });
            }}
          />
          <span>Journey Stages</span>
        </div>
        {isJourneyStagesExpanded && (
          <div className='collapsible-content'>
            <label>
              <input
                type='checkbox'
                checked={journeyStages.topFunnel}
                onChange={() =>
                  setJourneyStages((prev) => ({ ...prev, topFunnel: !prev.topFunnel }))
                }
              />
              Top-of-Funnel
            </label>
            <label>
              <input
                type='checkbox'
                checked={journeyStages.midFunnel}
                onChange={() =>
                  setJourneyStages((prev) => ({ ...prev, midFunnel: !prev.midFunnel }))
                }
              />
              Mid-Funnel
            </label>
            <label>
              <input
                type='checkbox'
                checked={journeyStages.botFunnel}
                onChange={() =>
                  setJourneyStages((prev) => ({ ...prev, botFunnel: !prev.botFunnel }))
                }
              />
              Bottom-Funnel
            </label>
          </div>
        )}
      </div>

      {/* Pipeline Predict */}
      <a href='/ai-scoring'>AI Scoring</a>
      <div className='collapsible-section'>
        <div
          className='collapsible-header'
          role={'button'}
          tabIndex={0}
          onClick={() => setIsPipelinePredictExpanded(!isPipelinePredictExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsPipelinePredictExpanded(!isPipelinePredictExpanded);
            }
          }}
        >
          <input
            ref={pipelinePredictRef}
            type='checkbox'
            checked={areAllSelected(pipelinePredict)}
            onChange={() => {
              const newState = !areAllSelected(pipelinePredict);
              setPipelinePredict({
                highlyLikely: newState,
                likely: newState,
                unlikely: newState,
              });
            }}
          />
          <span>Pipeline Predict</span>
        </div>
        {isPipelinePredictExpanded && (
          <div className='collapsible-content'>
            <label>
              <input
                type='checkbox'
                checked={pipelinePredict.highlyLikely}
                onChange={() =>
                  setPipelinePredict((prev) => ({ ...prev, highlyLikely: !prev.highlyLikely }))
                }
              />
              Highly Likely
            </label>
            <label>
              <input
                type='checkbox'
                checked={pipelinePredict.likely}
                onChange={() => setPipelinePredict((prev) => ({ ...prev, likely: !prev.likely }))}
              />
              Likely
            </label>
            <label>
              <input
                type='checkbox'
                checked={pipelinePredict.unlikely}
                onChange={() =>
                  setPipelinePredict((prev) => ({ ...prev, unlikely: !prev.unlikely }))
                }
              />
              Unlikely
            </label>
          </div>
        )}
      </div>

      {/* Qualification Scoring */}
      <div className='collapsible-section'>
        <div
          className='collapsible-header'
          role={'button'}
          tabIndex={0}
          onClick={() => setIsQualificationScoringExpanded(!isQualificationScoringExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsQualificationScoringExpanded(!isQualificationScoringExpanded);
            }
          }}
        >
          <input
            ref={qualificationScoringRef}
            type='checkbox'
            checked={areAllSelected(qualificationScoring)}
            onChange={() => {
              const newState = !areAllSelected(qualificationScoring);
              setQualificationScoring({
                highlyQualified: newState,
                qualified: newState,
                unqualified: newState,
              });
            }}
          />
          <span>Qualification Scoring</span>
        </div>
        {isQualificationScoringExpanded && (
          <div className='collapsible-content'>
            <label>
              <input
                type='checkbox'
                checked={qualificationScoring.highlyQualified}
                onChange={() =>
                  setQualificationScoring((prev) => ({
                    ...prev,
                    highlyQualified: !prev.highlyQualified,
                  }))
                }
              />
              Highly Qualified
            </label>
            <label>
              <input
                type='checkbox'
                checked={qualificationScoring.qualified}
                onChange={() =>
                  setQualificationScoring((prev) => ({ ...prev, qualified: !prev.qualified }))
                }
              />
              Qualified
            </label>
            <label>
              <input
                type='checkbox'
                checked={qualificationScoring.unqualified}
                onChange={() =>
                  setQualificationScoring((prev) => ({ ...prev, unqualified: !prev.unqualified }))
                }
              />
              Unqualified
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
